// 🗑️ Company Deletion Utility
// Handles complete deletion of company and all associated data

import { doc, deleteDoc, collection, getDocs, writeBatch, getDoc, setDoc } from 'firebase/firestore';

/**
 * Delete a company and ALL its data permanently
 * This is irreversible and removes:
 * - Company document
 * - All subcollections (employees, sales, purchases, etc.)
 * - Company from user_companies
 * - All related data
 * 
 * @param {Object} db - Firestore database instance
 * @param {string} companyId - Company ID to delete
 * @param {string} userId - User ID performing deletion (must be owner)
 * @returns {Promise<Object>} - Result of deletion
 */
export const deleteCompanyCompletely = async (db, companyId, userId) => {
  try {
    console.log('🗑️ Starting company deletion for:', companyId);
    console.log('🔐 Deletion requested by user:', userId);

    // 1. Verify user is the owner
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      return {
        success: false,
        error: 'Company not found'
      };
    }

    const companyData = companySnap.data();
    const ownerId = companyData.owner?.userId;

    if (ownerId !== userId) {
      console.error('❌ Unauthorized: User is not the owner');
      return {
        success: false,
        error: 'Only the company owner can delete the company'
      };
    }

    console.log('✅ Ownership verified');

    // 2. Delete all subcollections
    const subcollections = [
      'subscription_details',
      'team_members',
      'employees',
      'security',
      'sales',
      'purchases',
      'parties',
      'items',
      'payments',
      'receipts',
      'expenses',
      'employee_assignments',
      'role_assignments',
      'activity_logs',
      'usage_tracking'
    ];

    let deletedSubcollections = 0;
    let deletedDocuments = 0;

    for (const subcollectionName of subcollections) {
      try {
        const subcollectionRef = collection(db, 'companies', companyId, subcollectionName);
        const snapshot = await getDocs(subcollectionRef);
        
        if (!snapshot.empty) {
          const batch = writeBatch(db);
          snapshot.docs.forEach(doc => {
            batch.delete(doc.ref);
            deletedDocuments++;
          });
          await batch.commit();
          deletedSubcollections++;
          console.log(`🗑️ Deleted ${subcollectionName}: ${snapshot.size} documents`);
        }
      } catch (error) {
        console.warn(`⚠️ Could not delete ${subcollectionName}:`, error.message);
        // Continue with other subcollections
      }
    }

    console.log(`✅ Deleted ${deletedSubcollections} subcollections with ${deletedDocuments} documents`);

    // 3. Remove company from user_companies for all members
    try {
      const members = companyData.members || [userId];
      for (const memberId of members) {
        try {
          const userCompaniesRef = doc(db, 'user_companies', memberId);
          const userCompaniesSnap = await getDoc(userCompaniesRef);
          
          if (userCompaniesSnap.exists()) {
            const userData = userCompaniesSnap.data();
            
            // Filter out the deleted company
            const updatedCompanies = (userData.companies || []).filter(c => c.companyId !== companyId);
            const updatedOwnedCompanies = (userData.owned_companies || []).filter(c => c.companyId !== companyId);
            const updatedEmployedCompanies = (userData.employed_companies || []).filter(c => c.companyId !== companyId);
            
            // Update user_companies
            await deleteDoc(userCompaniesRef);
            if (updatedCompanies.length > 0 || updatedOwnedCompanies.length > 0 || updatedEmployedCompanies.length > 0) {
              await setDoc(userCompaniesRef, {
                companies: updatedCompanies,
                owned_companies: updatedOwnedCompanies,
                employed_companies: updatedEmployedCompanies,
                last_updated: new Date()
              });
            }
            
            console.log(`✅ Removed company from user ${memberId}'s companies list`);
          }
        } catch (error) {
          console.warn(`⚠️ Could not update user_companies for ${memberId}:`, error.message);
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not update all user_companies:', error.message);
    }

    // 4. Delete the main company document
    await deleteDoc(companyRef);
    console.log('✅ Deleted main company document');

    // 5. Log the deletion
    console.log('🎉 Company deletion completed successfully');
    
    return {
      success: true,
      message: 'Company and all associated data deleted successfully',
      stats: {
        subcollectionsDeleted: deletedSubcollections,
        documentsDeleted: deletedDocuments
      }
    };

  } catch (error) {
    console.error('❌ Error during company deletion:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Verify if user can delete a company
 * @param {Object} db - Firestore database instance
 * @param {string} companyId - Company ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Verification result
 */
export const canDeleteCompany = async (db, companyId, userId) => {
  try {
    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      return { canDelete: false, reason: 'Company not found' };
    }

    const companyData = companySnap.data();
    const ownerId = companyData.owner?.userId;

    if (ownerId !== userId) {
      return { canDelete: false, reason: 'Only the company owner can delete the company' };
    }

    return { canDelete: true };
  } catch (error) {
    return { canDelete: false, reason: error.message };
  }
};

